/**
 * Classifier Log Helpers
 *
 * Provides utilities and configurations for classifier log functionality.
 * Includes API request parameters, column configurations, and error handling.
 */

import { each, isEmpty } from 'lodash';
import { i18n } from '@lingui/core';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import {
    LABEL_CREDENTIAL,
    LABEL_LOG_MESSAGE,
    LABEL_PROTOCOL,
    LABEL_RESULT,
    LABEL_TIME_TAMP,
    MAPPING_CLASSIFIERLOG_CREDENTIAL_FIELD,
    MAPPING_CLASSIFIERLOG_PROTOCOL_FIELD,
    MAPPING_CLASSIFIERLOG_REASON_FIELD,
    MAPPING_CLASSIFIERLOG_SUCCESS_FIELD,
    MAPPING_CLASSIFIERLOG_TIME_FIELD,
    MAPPING_TEST_TIME_FIELD,
} from './InfrastructureConstants';
import { GENERIC_SERVER_ERROR } from '../../../services/FormErrorService';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { SORT_DESC } from '../../../utilities/FormUtilities';

const CLASSIFIER_LOG = 'classifierlog';
const INFR_LOG = 'infrlog';

/**
 * API endpoint for fetching classifier log data.
 */
export const CLASSIFIER_LOG_LIST_URL = `/query/${CLASSIFIER_LOG}`;

/**
 * Base request parameters for classifier log queries.
 */
const CLASSIFIER_LOG_LIST_REQUEST_BASE_PARAMS = {
    metrics: CLASSIFIER_LOG,
    type: INFR_LOG,
    rowStart: 0,
    rowLimit: 500,
};

/**
 * Returns request parameters for fetching classifier logs for a specific infrastructure item.
 *
 * @param {string|number} infrlogId - The infrastructure log ID
 * @returns {Object} Request parameters object
 */
export const getClassifierLogListRequestParams = (infrlogId) => {
    return {
        ...CLASSIFIER_LOG_LIST_REQUEST_BASE_PARAMS,
        [INFR_LOG]: infrlogId,
    };
};

const SUCCESS_RESULT = 'Success';
const FAIL_RESULT = 'Fail';

/**
 * Formats a boolean result value as "Success" or "Fail".
 *
 * @param {boolean} result - The result value
 * @returns {string} "Success" or "Fail"
 */
const formatResult = (result) => (result ? SUCCESS_RESULT : FAIL_RESULT);

/**
 * Default sort configuration for classifier log table.
 * Sorts by test time in descending order (newest first).
 */
export const CLASSIFIER_LOG_SORT_FIELD_INFO = [
    {
        property: MAPPING_TEST_TIME_FIELD,
        direction: SORT_DESC,
    },
];

/**
 * Column configuration for classifier log data table.
 */
export const CLASSIFIER_LOG_COLUMNS = [
    {
        type: COLUMN_TYPES.TEXT,
        title: LABEL_TIME_TAMP,
        formatter: FORMATTER_TYPES.DATETIME,
        width: 220,
        mapping: MAPPING_CLASSIFIERLOG_TIME_FIELD,
        sortable: true,
    },
    {
        type: COLUMN_TYPES.TEXT,
        width: 90,
        title: LABEL_RESULT,
        mapping: MAPPING_CLASSIFIERLOG_SUCCESS_FIELD,
        sortable: true,
        formatter: formatResult,
    },
    {
        type: COLUMN_TYPES.TEXT,
        width: 100,
        title: LABEL_PROTOCOL,
        mapping: MAPPING_CLASSIFIERLOG_PROTOCOL_FIELD,
        sortable: true,
    },
    {
        type: COLUMN_TYPES.TEXT,
        width: 150,
        title: LABEL_CREDENTIAL,
        mapping: MAPPING_CLASSIFIERLOG_CREDENTIAL_FIELD,
        sortable: true,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: MAPPING_CLASSIFIERLOG_REASON_FIELD,
        title: LABEL_LOG_MESSAGE,
        sortable: false,
        flex: 1,
    },
];

/**
 * Collects and formats error messages from data table action errors.
 * Extracts user-facing error messages or returns generic error if none available.
 *
 * @param {Array} dataTableActionErrors - Array of error objects from data table actions
 * @returns {Array<string>} Array of formatted error messages
 */
export const collectDataTableActionErrors = (dataTableActionErrors) => {
    const errors = [];
    let hasGenericError = false;

    each(dataTableActionErrors, (error) => {
        if (error && !isEmpty(error.userMessageKey)) {
            errors.push(error.userMessage);
        } else if (!hasGenericError) {
            hasGenericError = true;
            errors.push(i18n._(GENERIC_SERVER_ERROR));
        }
    });

    return errors;
};
